import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { saasModernTheme } from 'common/theme/saasModern';
import { useTranslations } from 'hooks/useTranslations';

const SocialProofSection = () => {
  const { t } = useTranslations();

  // Fallback data in case translations fail
  const fallbackData = {
    title: "Trusted by 29,000+ Users Worldwide",
    stats: {
      agents_deployed: "4,035+ AI Agents Successfully Deployed",
      interactions: "2M+ Agent Interactions Completed",
      enterprise_grade: "Proven Enterprise-Grade Platform"
    },
    problem_solution: {
      title: "Problems We Solve",
      items: [
        {
          problem: "Multiple AI subscriptions",
          solution: "One AI aggregator platform"
        },
        {
          problem: "6 months to build AI solutions", 
          solution: "Create agents in 10 minutes"
        },
        {
          problem: "Technical barriers for teams",
          solution: "No-code agent builder"
        },
        {
          problem: "No enterprise control",
          solution: "Advanced governance & SSO"
        },
        {
          problem: "Vendor lock-in fears",
          solution: "Provider-agnostic approach"
        }
      ]
    }
  };

  const getItems = (path, fallback) => {
    const items = t(path, { returnObjects: true });
    return Array.isArray(items) ? items : fallback;
  };

  const statsData = [
    {
      text: t('pricing.social_proof.stats.agents_deployed') || fallbackData.stats.agents_deployed
    },
    {
      text: t('pricing.social_proof.stats.interactions') || fallbackData.stats.interactions
    },
    {
      text: t('pricing.social_proof.stats.enterprise_grade') || fallbackData.stats.enterprise_grade
    }
  ];

  const problemSolutionItems = getItems('pricing.social_proof.problem_solution.items', fallbackData.problem_solution.items);

  return (
    <>
      {/* Modern Social Proof Section */}
      <Box sx={{ 
        py: 12, 
        background: saasModernTheme.customColor.gradiantColor,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(1px)',
        }
      }}>
        <Box sx={{ 
          maxWidth: '1400px', 
          mx: 'auto', 
          px: 4,
          position: 'relative',
          zIndex: 1
        }}>
          {/* Modern Title */}
          <Box sx={{ textAlign: 'center', mb: 12 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: ['28px', '40px', '52px'],
                fontFamily: saasModernTheme.fontFamily,
                fontWeight: 800,
                mb: 4,
                color: 'white',
                letterSpacing: '-0.03em',
                lineHeight: 1.2,
              }}
            >
              {t('pricing.social_proof.title') || fallbackData.title}
            </Typography>
          </Box>

          {/* Modern Stats Cards */}
          <Grid container spacing={4} justifyContent="center">
            {statsData.map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    p: 4,
                    textAlign: 'center',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: ['16px', '18px'],
                      fontWeight: 600,
                      color: 'white',
                      lineHeight: 1.5,
                    }}
                  >
                    {stat.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Modern Problem-Solution Section */}
      <Box sx={{ 
        py: 16, 
        backgroundColor: '#fafbfc',
        borderTop: '1px solid rgba(0,0,0,0.04)'
      }}>
        <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: ['24px', '32px', '40px'],
              fontFamily: saasModernTheme.fontFamily,
              fontWeight: 800,
              mb: 12,
              textAlign: 'center',
              color: saasModernTheme.colors.primary,
              letterSpacing: '-0.025em',
              lineHeight: 1.2,
            }}
          >
            {t('pricing.social_proof.problem_solution.title') || fallbackData.problem_solution.title}
          </Typography>

          <Grid container spacing={3}>
            {problemSolutionItems.map((item, index) => (
              <Grid item xs={12} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: ['wrap', 'nowrap'],
                    gap: 4,
                    py: 4,
                    px: 5,
                    backgroundColor: 'white',
                    borderRadius: '20px',
                    border: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                      borderColor: 'rgba(16, 152, 247, 0.2)',
                    }
                  }}
                >
                  {/* Problem */}
                  <Box sx={{ flex: 1, minWidth: ['100%', '42%'] }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: ['15px', '16px'],
                        fontWeight: 500,
                        color: saasModernTheme.colors.gray,
                        textAlign: ['center', 'left'],
                        lineHeight: 1.6,
                        position: 'relative',
                        '&:before': {
                          content: '"⚠️"',
                          marginRight: '8px',
                          fontSize: '16px',
                        }
                      }}
                    >
                      {item.problem}
                    </Typography>
                  </Box>

                  {/* Modern Arrow */}
                  <Box sx={{ 
                    display: ['none', 'flex'], 
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(16, 152, 247, 0.1)',
                    borderRadius: '50%',
                    color: saasModernTheme.colors.primaryLight,
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    →
                  </Box>

                  {/* Solution */}
                  <Box sx={{ flex: 1, minWidth: ['100%', '42%'] }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: ['15px', '16px'],
                        fontWeight: 600,
                        color: saasModernTheme.colors.primary,
                        textAlign: ['center', 'right'],
                        lineHeight: 1.6,
                        position: 'relative',
                        '&:before': {
                          content: '"✅"',
                          marginRight: '8px',
                          fontSize: '16px',
                        }
                      }}
                    >
                      {item.solution}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default SocialProofSection;
